import axios, { AxiosInstance } from "axios";
import {
  getJwt,
  setCustomerCookie,
  getCustomerCookie,
  eraseCookie,
  eraseChemCookies,
  eraseCookies,
} from "./cookie-helpers";
import { setPermissions } from "./permissions";
import {
  getCustomerQuery,
  getUserQuery,
  locationsUserPermissionsQuery,
} from "./queries";
import { Customer, Permissions, User } from "./types";
import { getAuthRedirectUrl } from "./AuthHelpers";
import { PendoService } from "./PendoService";
import { GainsightService } from "./GainsightService";
import I18nService from "./i18nService";
import AuthService from "./services/AuthService";

type EventBusCallback = (data: any) => void;

interface EventBusEvents {
  [eventName: string]: EventBusCallback[];
}

interface FeatureFlags {
  includeIndErgoLogout?: boolean;
  [key: string]: boolean | undefined;
}

class UsersService {
  api: AxiosInstance;
  i18nService: I18nService;
  user: User | null;
  customer: Customer | null;
  pendo: PendoService;
  gainsight: GainsightService;
  authService: AuthService;
  permissions: Permissions | null;
  activeCustomerId: string = "";
  events: EventBusEvents;
  private JWT: string = "";
  private userPromise = null;

  constructor(user = null, customer = null, permissions = null, events = {}) {
    this.user = user;
    this.customer = customer;
    this.permissions = permissions;
    this.events = events;
    this.api = axios.create({
      baseURL: process.env.GRAPHQL_GATEWAY_URL,
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "apollographql-client-name": "accelerate-frontend",
      },
    });
    this.authService = AuthService.getInstance();

    this.pendo = new PendoService();
    this.gainsight = new GainsightService();

    this.addBearerTokenInterceptor(this.api);

    this.addGraphQLErrorInterceptor(this.api);
  }

  public initSessionUser = async (featureFlags: { [key: string]: boolean }) => {
    if (this.userPromise) return this.userPromise;
    if (this.user && this.activeCustomerId) return Promise.resolve();
    this.userPromise = this.handleinitSessionUser(featureFlags);
    return this.userPromise;
  };

  private handleinitSessionUser = async (featureFlags: {
    [key: string]: boolean;
  }) => {
    try {
      this.JWT = getJwt();
      if (!this.JWT) {
        return this.logout(featureFlags);
      }

      const user = await this.getUser();
      const customerId = this.getCustomerId(user.userCustomerId);

      const [locationsPermissions] = await Promise.all([
        this.getLocationsPermissions(user),
        this.setActiveCustomerCookie(customerId),
      ]);

      const permissions = {
        ...user.permissions.accelerate,
        ...locationsPermissions,
      };

      this.permissions = setPermissions(permissions);
      delete user.permissions;
      this.setPreferredLanguage(user);
      this.setPreferredTimeFormat(user);
      // Only store the final calculated user preference values w/ defaults applied
      delete user.userPreferences;
      this.user = user as User;
      // Once user data is set, get translations if LD enableTranslationsAPI flag is true
      if (featureFlags.enableTranslationsAPI) {
        this.i18nService = new I18nService(
          user.userCustomerId,
          user.preferredLanguage
        );
        await this.i18nService.init();
      }
      if (featureFlags.enablePendo) {
        this.pendo.initialize(this.user, this.customer);
      }
      if (featureFlags.enableGainsightPX) {
        this.gainsight.initialize(this.user, this.customer);
      }
    } catch (error) {
      this.logout(featureFlags);
      throw error;
    }
  };

  public refreshSessionUser = (updatedUser: User) => {
    this.user = { ...this.user, ...updatedUser };
  };

  public setActiveCustomerCookie = async (customerId: string) => {
    setCustomerCookie(customerId);
    this.activeCustomerId = customerId;
    await this.getCustomer(customerId);
  };

  public isUserCustomerActive = () => {
    if (!this.user || !this.customer) return false;
    return this.user.userCustomerId === this.customer.id;
  };

  public logout = async (featureFlags: FeatureFlags): Promise<void> => {
    // Handle industrial ergonomics logout if enabled
    if (featureFlags.includeIndErgoLogout) {
      await this.handleErgoLogout();
    }

    // Handle Accelerate core logout
    await this.performLogoutActions();
  };

  private handleErgoLogout = async (): Promise<void> => {
    if (!this.user) return;
    const assignedSolutions = this.user?.assignedSolutions;
    const indErgoSolution = assignedSolutions.find(
      (solution) => solution?.code === "INDUSTRIAL_ERGONOMICS"
    );
    const ergoApplication = indErgoSolution?.applications?.find(
      (app) => app?.applicationCode === "ergo"
    );
    const ergoEmbedUrl = ergoApplication?.embedUrl;
    if (!ergoEmbedUrl) return;

    try {
      await axios.post(`${ergoEmbedUrl}/logout`, null, {
        withCredentials: true,
      });
    } catch (error) {
      console.error(`Error calling: ${ergoEmbedUrl}/logout`, error);
      throw error; // Re-throw to be handled by the main logout function
    }
  };

  private performLogoutActions = async (): Promise<void> => {
    // Clear cookies
    eraseCookie("userslocation.label");
    eraseCookie("userslocation.id");
    eraseCookies();
    eraseChemCookies();

    // Notify subscribers
    this.publish("LOGOUT", undefined);

    try {
      // Redirect to the auth logout URL
      window.location.href = getAuthRedirectUrl().href;
    } catch (error) {
      console.error("Error during logout redirect:", error);
    }
  };

  private getUser = async () => {
    try {
      const userInfo = await this.authService.getUserInfoData();
      const response = await this.api.post(
        "",
        getUserQuery(userInfo.velocityUserID, this.getCustomerId())
      );

      const user = {
        ...response.data.data.user,
        ...response.data.data.user.userDemographic,
      };

      user.baseLocationId = user.customer.baseLocation.id;
      user.userCustomerId = user.customer.id;
      delete user.userDemographic;
      delete user.customer;

      return user;
    } catch (error) {
      console.error("Error fetching user data:", error);
      throw error;
    }
  };

  private getLocationsPermissions = async (user: User) => {
    const response = await this.api.post(
      "",
      locationsUserPermissionsQuery(
        user.id,
        this.getCustomerId(user.userCustomerId)
      )
    );
    return response.data.data.locations_user;
  };

  private getCustomer = async (id: string) => {
    const response = await this.api.post("", getCustomerQuery(id));

    this.customer = response.data.data.customer;
  };

  private getCustomerId = (userCustomerId?: string) => {
    return getCustomerCookie() || userCustomerId || "";
  };

  private addBearerTokenInterceptor(api: AxiosInstance) {
    api.interceptors.request.use((config) => {
      //@ts-ignore
      config.headers["Authorization"] = "Bearer " + getJwt();
      return config;
    });
  }

  private addGraphQLErrorInterceptor(api: AxiosInstance) {
    api.interceptors.response.use(
      (response) => {
        const errors = response?.data?.errors;
        if (errors) {
          throw { response };
        } else {
          return response;
        }
      },
      (error) => {
        throw error;
      }
    );
  }

  // Helper function for returning the UserPreference value associated with the passed in key
  private getUserPreference(user: any, key: string) {
    const userPreference = user.userPreferences.find(
      (userPref) => userPref.key === key
    )?.value;
    return userPreference;
  }

  // Set preferred language from userPreferences. If none then default to english
  public setPreferredLanguage = (user: User) => {
    const userPreferenceLanguage = this.getUserPreference(user, "language");
    user.preferredLanguage = userPreferenceLanguage || "en";
    return;
  };

  // Set preferred time format from userPreferences. If none then default to 12-hour time format
  public setPreferredTimeFormat = (user: User) => {
    const userPreferenceTimeFormat = this.getUserPreference(user, "timeFormat");
    user.preferredTimeFormat = userPreferenceTimeFormat || "12-hour";
    return;
  };

  // EVENT BUS
  public subscribe(eventName: string, callback: EventBusCallback): void {
    if (!this.events[eventName]) {
      this.events[eventName] = [];
    }
    this.events[eventName].push(callback);
  }

  public publish(eventName: string, data: any): void {
    const event = this.events[eventName];
    if (event) {
      event.forEach((callback) => callback(data));
    }
  }

  public unsubscribe(eventName: string, callback: EventBusCallback): void {
    const event = this.events[eventName];
    if (event) {
      this.events[eventName] = event.filter((cb) => cb !== callback);
    }
  }
}

export default new UsersService();
